<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { useScroll } from '@vueuse/core';

import type { AppMaskType } from '@libs/ui';
import { AppMask, arrivedStateToMaskType } from '@libs/ui';

import type { MonthsSelectorMonth } from '../monthsSelector';

import DayShortName from './components/DayShortName.vue';
import PriceCalendarTileView from './components/PriceCalendarTileView.vue';
import type { PriceCalendarState } from './helpers';
import { PriceCalendarTileType } from './models';

interface PriceCalendarSingleMonthRibbonProps {
  selectedMonth: string;
  months: MonthsSelectorMonth[];
  priceCalendarState: PriceCalendarState;
  showPrices?: boolean;
  allowSelectionFor?: PriceCalendarTileType[];
}

const props = withDefaults(defineProps<PriceCalendarSingleMonthRibbonProps>(), {
  showPrices: true,
  allowSelectionFor: () => [
    PriceCalendarTileType.CurrentMonthAvailableDay,
    PriceCalendarTileType.CurrentMonthAlternativeProductDay,
    PriceCalendarTileType.CurrentMonthUnavailableDay,
  ],
});

interface PriceCalendarSingleMonthRibbonEmits {
  (eventName: 'select-date', date: Date): void;
  (eventName: 'select-month', month: Date): void;
}

const emit = defineEmits<PriceCalendarSingleMonthRibbonEmits>();

const ribbonRef = useTemplateRef('ribbonRef');

const { arrivedState, x: xScroll } = useScroll(ribbonRef, {
  behavior: 'smooth',
});

const maskType = computed<AppMaskType>(() =>
  arrivedStateToMaskType(arrivedState)
);

function getScrollPosition(dayIndex: number): number {
  const translation = 120;
  const tileSize = 57;
  return Math.max(dayIndex * tileSize - translation, 0);
}

const longAnimationTime = 700;
setTimeout(() => {
  xScroll.value = getScrollPosition(
    props.priceCalendarState.getSelectedOrSuggestedDayIndexFromAvailableDaysOnly()
  );
}, longAnimationTime);
</script>

<template>
  <AppMask :type="maskType">
    <div ref="ribbonRef" class="price-calendar-single-month-ribbon">
      <div
        v-for="(
          tile, calendarRowIndex
        ) of props.priceCalendarState.getCurrentMonthTiles()"
        :key="calendarRowIndex"
        class="tile"
      >
        <DayShortName :date="tile.date" />
        <PriceCalendarTileView
          :calendar-tile="tile"
          :show-prices="props.showPrices"
          :allow-selection-for="props.allowSelectionFor"
          @select-date="emit('select-date', $event)"
        />
      </div>
    </div>
  </AppMask>
</template>

<style scoped lang="scss">
.price-calendar-single-month-ribbon {
  // NOTE: Variables:
  // * calendar-weekday-font
  // * calendar-tile-day-font
  // * calendar-tile-day-font
  // * calendar-tile-price-font
  // are set via mixins in /modules/priceCalendar/styles/_price_calendar_mixins.scss
  --calendar-tile-size: 100%;
  --calendar-tile-background-color: var(--color-white);
  --calendar-tile-border-radius: var(--border-radius-8);
  --calendar-tile-day-font-color: var(--color-neutral-70);
  --calendar-tile-past-day-font-color: var(--color-neutral-30);
  --calendar-tile-hover-color: var(--color-neutral-10);
  --calendar-tile-info-icon-right: 6px;
  --calendar-tile-info-icon-right-mobile: -16px;
  --calendar-tile-info-icon-top: -8px;
  --calendar-tile-price-font-color: var(--color-neutral-70);
  --calendar-tile-gap: 0px;
  --calendar-tile-day-price-gap: var(--spacing-8);
  --calendar-weekday-font-color: var(--color-neutral-70);
  --calendar-price-placeholder-height: 16px;

  width: 100%;
  height: 83px;

  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  overflow-x: auto;

  @include no-scroll;

  .tile {
    width: 57px;
    min-width: 57px;
    height: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: var(--spacing-2);
  }
}
</style>
